import { Checkbox, Form, Input, Modal, Select } from 'antd';
import { useEffect } from 'react';

import { AdminUser } from '@/types/apis/admin';

const { Option } = Select;

interface AdminUserWithPassword
  extends Omit<AdminUser, 'createdAt' | 'updatedAt'> {
  password: string;
}

type Props = {
  isOpen: boolean;
  user?: AdminUser | AdminUserWithPassword | null;
  onClose: () => void;
  onSave: (user: AdminUser | AdminUserWithPassword) => void;
  mode: 'add' | 'edit';
};

export default function UserModal({
  isOpen,
  user,
  onClose,
  onSave,
  mode,
}: Props) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && user) {
        form.setFieldsValue({
          name: user.name,
          email: user.email,
          provider: user.provider,
          avatarUrl: user.avatarUrl,
          isVerified: user.isVerified,
          isAdmin: user.isAdmin,
        });
      } else if (mode === 'add') {
        form.resetFields();
        form.setFieldsValue({
          provider: 'local',
          isVerified: false,
          isAdmin: false,
        });
      }
    }
  }, [user, mode, isOpen, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      if (mode === 'edit' && user) {
        const updateData = {
          ...user,
          ...values,
        };
        onSave(updateData);
      } else {
        const newData = {
          id: '',
          ...values,
        };
        onSave(newData);
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={mode === 'edit' ? '編輯用戶' : '新增用戶'}
      open={isOpen}
      onOk={handleSave}
      onCancel={handleCancel}
      okText="儲存"
      cancelText="取消"
      width={600}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          label="用戶名稱"
          name="name"
          rules={[{ required: true, message: '請輸入用戶名稱' }]}
        >
          <Input placeholder="請輸入用戶名稱..." />
        </Form.Item>

        <Form.Item
          label="電子郵件"
          name="email"
          rules={[
            { required: true, message: '請輸入電子郵件' },
            { type: 'email', message: '請輸入有效的電子郵件地址' },
          ]}
        >
          <Input placeholder="請輸入電子郵件地址..." />
        </Form.Item>

        {mode === 'add' && (
          <Form.Item
            label="密碼"
            name="password"
            rules={[{ required: true, message: '請輸入密碼' }]}
          >
            <Input.Password placeholder="請輸入密碼..." />
          </Form.Item>
        )}

        <Form.Item label="註冊方式" name="provider">
          <Select>
            <Option value="local">本地註冊</Option>
            <Option value="google">Google</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="頭像網址"
          name="avatarUrl"
          rules={[{ type: 'url', message: '請輸入有效的網址' }]}
        >
          <Input placeholder="請輸入頭像圖片網址..." />
        </Form.Item>

        <Form.Item>
          <div>
            <Form.Item
              name="isVerified"
              valuePropName="checked"
              style={{ marginBottom: 8 }}
            >
              <Checkbox>已驗證帳戶</Checkbox>
            </Form.Item>

            <Form.Item
              name="isAdmin"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Checkbox>管理員權限</Checkbox>
            </Form.Item>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
}
